import React, { useEffect, useState, useRef } from 'react';
import { medicalApi } from '../../services/api';
import './TaskStatus.css';

const POLL_INTERVAL_MS = 3000;          // как часто спрашиваем статус
const MAX_WAIT_MS = 30 * 60 * 1000;     // максимум ожидания обработки: 30 минут (реальное время)
const MAX_OUTAGE_MS = 10 * 60 * 1000;   // сколько терпим недоступность сервера подряд (перезапуск, сеть)

const fmt = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${Math.floor(s / 60)} мин ${s % 60} сек`;
};

const TaskStatus = ({ taskId, onTaskComplete, onTaskFailed }) => {
  const [status, setStatus] = useState('pending');
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [pollingCount, setPollingCount] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(180);
  const [elapsed, setElapsed] = useState(0);        // секунды реального времени
  const [reconnecting, setReconnecting] = useState(false);

  const stoppedRef = useRef(false);
  const cbRef = useRef({ onTaskComplete, onTaskFailed });
  cbRef.current = { onTaskComplete, onTaskFailed };

  // Один цикл опроса на задачу: не пересоздаётся при смене состояния.
  useEffect(() => {
    if (!taskId) return undefined;
    stoppedRef.current = false;
    const startedAt = Date.now();
    let timer = null;
    let failingSince = null;

    const stop = (finalError) => {
      stoppedRef.current = true;
      if (timer) clearTimeout(timer);
      setIsCompleted(true);
      setReconnecting(false);
      if (finalError) setError(finalError);
    };

    const tick = async () => {
      if (stoppedRef.current) return;
      setElapsed((Date.now() - startedAt) / 1000);
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        stop('Превышено время ожидания обработки (30 минут)');
        return;
      }
      try {
        const data = await medicalApi.getTaskStatus(taskId);
        if (stoppedRef.current) return;
        if (data && data.status === 'unreachable') {
          // сайт не достучался до сервера сегментации: задача на нём продолжается
          throw new Error('unreachable');
        }
        failingSince = null;
        setReconnecting(false);
        setPollingCount((n) => n + 1);
        setStatus(data.status);
        setMessage(data.message || '');
        if (data.estimated_time) setEstimatedTime(data.estimated_time);
        if (typeof data.progress === 'number') setProgress(data.progress);

        if (data.status === 'completed') {
          stop();
          if (cbRef.current.onTaskComplete) cbRef.current.onTaskComplete(data);
          return;
        }
        if (data.status === 'failed' || data.status === 'cancelled') {
          stop(data.message || 'Ошибка обработки');
          if (cbRef.current.onTaskFailed) cbRef.current.onTaskFailed(data);
          return;
        }
      } catch (err) {
        if (stoppedRef.current) return;
        const code = err && err.response && err.response.status;
        if (code === 404) {
          stop('Задача не найдена на сервере (возможно, истёк срок хранения). Загрузите файл заново.');
          return;
        }
        // 503/502/сеть: сервер перезапускается или временно недоступен — продолжаем пробовать
        failingSince = failingSince || Date.now();
        setReconnecting(true);
        if (Date.now() - failingSince > MAX_OUTAGE_MS) {
          stop('Сервер обработки не отвечает. Обновите страницу позже: результат сохранится на сервере.');
          return;
        }
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => {
      stoppedRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [taskId]);

  const getStatusColor = () => {
    switch (status) {
      case 'processing': return '#ffc107';
      case 'completed': return '#28a745';
      case 'failed': return '#dc3545';
      default: return '#007bff';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'pending': return 'Ожидание';
      case 'processing': return 'Обработка';
      case 'completed': return 'Завершено';
      case 'failed': return 'Ошибка';
      default: return status;
    }
  };

  const getElapsedTime = () => fmt(elapsed);
  const getRemainingTime = () => fmt(Math.max(0, estimatedTime - elapsed));

  const handleCancelTask = async () => {
    try {
      await medicalApi.cancelTask(taskId);
      stoppedRef.current = true;
      setIsCompleted(true);
      setError('Задача была отменена пользователем');
    } catch (error) {
      console.error('Ошибка при отмене задачи:', error);
      setError('Не удалось отменить задачу');
    }
  };

  return (
    <div className="task-status">
      <div className="task-header">
        <h4>Статус обработки</h4>
        <div className="task-id">ID: {taskId}</div>
      </div>
      
      <div className="polling-info">
                <div>Прошло: {getElapsedTime()}</div>
        {status === 'processing' && (
          <div>Осталось: ~{getRemainingTime()}</div>
        )}
      </div>
      
      <div className="status-info">
        <div className="status-badge" style={{ backgroundColor: getStatusColor() }}>
          {getStatusText()}
        </div>
        
        {(message || error) && (
          <div className={`status-message ${error ? 'error' : ''}`}>
            {error || message}
          </div>
        )}
      </div>

      {status === 'processing' && (
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="progress-text">{progress}%</span>
          <div className="time-estimate">
            Максимальное время ожидания: 30 минут
          </div>
        </div>
      )}

      {status === 'processing' && (
        <div className="task-actions">
          <button 
            onClick={handleCancelTask}
            className="cancel-btn"
            disabled={elapsed < 10}
          >
            Отменить обработку
          </button>
        </div>
      )}

      {status === 'completed' && (
        <div className="success-message">
          ✅ Обработка завершена успешно! Модель готова для просмотра и скачивания.
        </div>
      )}

      {status === 'failed' && (
        <div className="error-message">
          ❌ Произошла ошибка при обработке. Пожалуйста, попробуйте еще раз.
        {error && <div className="error-detail">{error}</div>}
        </div>
      )}

      {reconnecting && !isCompleted && (
        <div className="info-message">
          🔄 Сервер обработки временно недоступен (возможно, перезапускается). Повторяем попытки, задача не потеряна.
        </div>
      )}

      {isCompleted && status !== 'completed' && status !== 'failed' && (
        <div className="warning-message">
          ⚠️ Опрос статуса остановлен. Обновите страницу для повторной проверки.
        </div>
      )}

      {elapsed > 180 && status === 'processing' && (
        <div className="info-message">
          ⏳ Обработка занимает больше обычного времени. Это нормально для больших файлов.
        </div>
      )}
    </div>
  );
};

export default TaskStatus;